import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { flushSync } from 'react-dom';

export type Theme = 'light' | 'dark';
/** What the member chose (Plan BI): a theme, or whatever the device uses ("Theo máy"). */
export type ThemePreference = Theme | 'system';

const STORAGE_KEY = 'hbac-theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

interface ThemeValue {
  theme: Theme;
  preference: ThemePreference;
  toggle: () => void;
  setPreference: (p: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeValue>({ theme: 'light', preference: 'system', toggle: () => {}, setPreference: () => {} });

export const useTheme = (): ThemeValue => useContext(ThemeContext);

/** The stored choice; nothing stored (or blocked storage) is the device's. Read the same way by index.html. */
export function readPreference(stored: string | null): ThemePreference {
  return stored === 'light' || stored === 'dark' ? stored : 'system';
}

const deviceTheme = (): Theme => (window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light');

/**
 * Seeded from the class the pre-paint script in index.html already set, so the
 * first render agrees with what the user is looking at and nothing flashes.
 * "Theo máy" follows the device live; the header's button picks the other theme outright.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => (document.documentElement.classList.contains('dark') ? 'dark' : 'light'));
  const [preference, setPref] = useState<ThemePreference>(() => {
    try {
      return readPreference(localStorage.getItem(STORAGE_KEY));
    } catch {
      return 'system';
    }
  });
  const current = useRef(theme);
  current.current = theme;

  const show = useCallback((next: Theme) => {
    if (next === current.current) return;
    const root = document.documentElement;
    const apply = () => {
      // Every element changes in the same frame. Otherwise the few with
      // transition-colors fade while the rest snap, which reads as flicker.
      root.classList.add('theme-switching');
      flushSync(() => setTheme(next));
      root.classList.toggle('dark', next === 'dark');
      void root.offsetHeight;
      requestAnimationFrame(() => root.classList.remove('theme-switching'));
    };
    const doc = document as Document & { startViewTransition?: (cb: () => void) => unknown };
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // A cross-fade of the whole page where the browser supports it.
    if (doc.startViewTransition && !reduced) doc.startViewTransition(apply);
    else apply();
  }, []);

  const setPreference = useCallback(
    (p: ThemePreference) => {
      setPref(p);
      try {
        if (p === 'system') localStorage.removeItem(STORAGE_KEY);
        else localStorage.setItem(STORAGE_KEY, p);
      } catch {
        // Private mode / blocked storage: the choice simply does not persist.
      }
      show(p === 'system' ? deviceTheme() : p);
    },
    [show],
  );

  // "Theo máy": the device switching to dark at sunset switches the app with it.
  useEffect(() => {
    if (preference !== 'system') return;
    const mql = window.matchMedia(DARK_QUERY);
    const onChange = () => show(mql.matches ? 'dark' : 'light');
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [preference, show]);

  const toggle = useCallback(() => setPreference(current.current === 'dark' ? 'light' : 'dark'), [setPreference]);

  return <ThemeContext.Provider value={{ theme, preference, toggle, setPreference }}>{children}</ThemeContext.Provider>;
}
